/**
 * Platform Navigation Store
 *
 * Manages navigation state for the dynamic platform menu.
 * Handles fetching navigation items from API, building tree structure,
 * and tracking active navigation state.
 */

import handleNavigation from '@/utils/navigationUtils';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import LayoutConstants from '@/constansts/layoutConstants';
import { i18n } from '@/plugins/i18n';
import platformNavigationService from '../services/platformNavigationService';
import {
  buildNavigationTree,
  getRootItem,
  getTopLevelItems,
  findItemByUrl,
  findItemByUid,
  getChildrenOf,
  hasChildren,
  findTopLevelParent,
  isExternalUrl,
  resolveMatterTerms,
  findBestMatchingItem,
  findItemByProperty,
  getItemNavigationUrl,
} from '../utils/navigationUtils';

/**
 * Get matter term translations using i18n
 * Uses LayoutConstants.MATTER_TRANSLATIONS_KEYS for translation keys
 * @param {string} matterTerm - The matter term key (e.g., 'clients', 'patients')
 * @returns {Object} Object with singular/plural values from translations
 */
const getMatterTerms = (matterTerm) => {
  const translationKeys = LayoutConstants.MATTER_TRANSLATIONS_KEYS[matterTerm]
    || LayoutConstants.MATTER_TRANSLATIONS_KEYS.clients;

  return {
    singular: i18n.t(translationKeys.lowercase_singular),
    plural: i18n.t(translationKeys.lowercase_plural),
    capitalizedSingular: i18n.t(translationKeys.capitalized_singular),
    capitalizedPlural: i18n.t(translationKeys.capitalized_plural),
    uppercaseSingular: i18n.t(translationKeys.uppercase_singular),
    uppercasePlural: i18n.t(translationKeys.uppercase_plural),
  };
};

const state = {
  rawNavigationItems: [],
  navigationTree: [],
  activeTopBarItemUid: null,
  currentRoutePath: null,
  activeItemIsBMP: false,
  showSidebar: false,
  isLoading: false,
  hasFatalError: false,
};

const getters = {
  /**
   * Get the root item (logo) from the navigation tree
   */
  rootItem: (storeState) => getRootItem(storeState.navigationTree),

  /**
   * Get top-level navigation items (direct children of logo)
   */
  topLevelItems: (storeState) => getTopLevelItems(storeState.navigationTree),

  /**
   * Get resolved item title with matter term substitution
   * Usage: getItemTitle(item) - returns localized title with matter terms resolved
   */
  getItemTitle: (storeState, storeGetters, rootState, rootGetters) => (item) => {
    if (!item || !item.title) return '';
    const locale = rootGetters['BusinessStore/locale'];
    const matterTerm = rootGetters['BusinessStore/matterTerm'];
    const matterTerms = getMatterTerms(matterTerm);
    const rawTitle = item.title[locale] || item.title.en || '';
    return resolveMatterTerms(rawTitle, matterTerms);
  },

  /**
   * Get children of a specific item by UID
   */
  getChildrenOf: (storeState) => (uid) => getChildrenOf(storeState.navigationTree, uid),

  /**
   * Get the full navigation tree
   */
  navigationTree: (storeState) => storeState.navigationTree,

  /**
   * Get the UID of the currently active top bar item
   */
  activeTopBarItemUid: (storeState) => storeState.activeTopBarItemUid,

  /**
   * Get the currently active top bar item
   */
  activeTopBarItem: (storeState) => {
    if (!storeState.activeTopBarItemUid) return null;
    return findItemByUid(storeState.navigationTree, storeState.activeTopBarItemUid);
  },

  /**
   * Get sidebar items (children of active top bar item)
   * Mapped to MainMenuWide expected format with matter term resolution
   */
  sidebarItems: (storeState, storeGetters, rootState, rootGetters) => {
    const activeItem = storeGetters.activeTopBarItem;
    if (!activeItem || !activeItem.children) return [];

    const locale = rootGetters['BusinessStore/locale'];
    const matterTerm = rootGetters['BusinessStore/matterTerm'];
    const matterTerms = getMatterTerms(matterTerm);
    const activeSidebarItem = storeGetters.activeSidebarItem;

    // Map items to MainMenuWide expected format with matter term resolution
    const mapToMenuFormat = (item) => {
      const rawLabel = item.title[locale] || item.title.en || '';
      const resolvedLabel = resolveMatterTerms(rawLabel, matterTerms);

      // Check if this item or any of its children is the active sidebar item
      const isActive = activeSidebarItem && (
        activeSidebarItem.uid === item.uid
        || (item.children && item.children.some((child) => activeSidebarItem.uid === child.uid))
      );

      return {
        uid: item.uid,
        name: item.key || item.uid,
        label: resolvedLabel,
        action: item.action || null,
        icon: item.icon || '',
        isActive,
        submenuItems: (item.children || []).map(mapToMenuFormat),
      };
    };

    return activeItem.children.map(mapToMenuFormat);
  },

  /**
   * Check if the active item is a BMP (Business Management Platform) item
   * BMP item shows sidebar children, or the old menu system if it has no children.
   */
  isActiveItemBMP: (storeState) => storeState.activeItemIsBMP,

  shouldShowOldMenu: (storeState, storeGetters) => {
    if (!storeState.activeItemIsBMP) return false;
    const activeItem = storeGetters.activeTopBarItem;
    return !hasChildren(activeItem);
  },

  shouldShowSidebar: (storeState, storeGetters) => {
    const activeItem = storeGetters.activeTopBarItem;
    return hasChildren(activeItem);
  },

  /**
   * Get normalized URL paths for all non-BMP navigation items (top-level and their descendants).
   * Used to filter duplicate entries from the BMP (old menu) sidebar when platform navigation is active.
   * External URLs (http/https) and BMP items are excluded.
   * Query params are stripped so matching works regardless of tracking params.
   * @returns {string[]} - Array of normalized paths like ['/app/dashboard', '/app/calendar']
   */
  nonBmpNavigationPaths: (storeState) => {
    const paths = [];
    const collect = (items) => {
      items.forEach((item) => {
        if (item.isBMP) return;
        const url = getItemNavigationUrl(item);
        if (url && !isExternalUrl(url)) paths.push(url.split('?')[0]);
        if (item.children && item.children.length > 0) collect(item.children);
      });
    };
    collect(getTopLevelItems(storeState.navigationTree));
    return paths;
  },

  /**
   * Get the active sidebar item based on current route
   * Used to highlight the correct sidebar item for entity pages
   * @returns {Object|null} - Active sidebar item or null
   */
  activeSidebarItem: (storeState, storeGetters) => {
    const activeTopItem = storeGetters.activeTopBarItem;
    if (!activeTopItem || !activeTopItem.children || !storeState.currentRoutePath) {
      return null;
    }

    // Find the best matching child item using current route path
    return findBestMatchingItem(activeTopItem.children, storeState.currentRoutePath);
  },

  /**
   * Get items available for mobile navigation
   */
  mobileItems: (storeState) => {
    const filterMobile = (items) => {
      const result = [];
      items.forEach((item) => {
        if (item.available_in_mobile) {
          const mobileItem = { ...item };
          if (item.children && item.children.length > 0) {
            mobileItem.children = filterMobile(item.children);
          }
          result.push(mobileItem);
        }
      });
      return result;
    };
    return filterMobile(getTopLevelItems(storeState.navigationTree));
  },

  /**
   * Get favorite items for mobile navigation
   */
  mobileFavoriteItems: (storeState) => {
    const favorites = [];
    const findFavorites = (items) => {
      items.forEach((item) => {
        if (item.favorite_in_mobile) {
          favorites.push(item);
        }
        if (item.children && item.children.length > 0) {
          findFavorites(item.children);
        }
      });
    };
    findFavorites(storeState.navigationTree);
    return favorites;
  },

  /**
   * Get item by UID
   */
  getItemByUid: (storeState) => (uid) => findItemByUid(storeState.navigationTree, uid),

  /**
   * Get item by URL
   */
  getItemByUrl: (storeState) => (url) => findItemByUrl(storeState.navigationTree, url),

  /**
   * Check if loading
   */
  isLoading: (storeState) => storeState.isLoading,

  /**
   * Check if there was a fatal error
   */
  hasFatalError: (storeState) => storeState.hasFatalError,

  showSidebar: (storeState) => storeState.showSidebar,
};

const mutations = {
  /**
   * Set navigation data from API response
   */
  setNavigationData(state, items) {
    state.rawNavigationItems = items;
    state.navigationTree = buildNavigationTree(items);
  },

  /**
   * Set the active top bar item and notify Angular about sidebar visibility
   */
  setActiveTopBarItem(state, uid) {
    state.activeTopBarItemUid = uid;

    // Find the active item and check if it has children (sidebar items) or is BMP
    const activeItem = findItemByUid(state.navigationTree, uid);
    const isBMP = activeItem && activeItem.isBMP === true;
    state.activeItemIsBMP = isBMP;

    // Show sidebar if item has children OR is BMP
    const showSidebar = (activeItem && hasChildren(activeItem)) || isBMP;
    state.showSidebar = showSidebar;

    // Notify Angular to show/hide sidebar padding
    outgoingPostMessageUtils.post('toggle-sidebar-visibility', { showSidebar });
  },

  /**
   * Set current route path for sidebar item highlighting
   */
  setCurrentRoutePath(state, path) {
    state.currentRoutePath = path;
  },

  /**
   * Set loading state
   */
  setLoading(state, isLoadingValue) {
    state.isLoading = isLoadingValue;
  },

  /**
   * Set fatal error state
   */
  setFatalError(state, hasFatalErrorValue) {
    state.hasFatalError = hasFatalErrorValue;
  },

  /**
   * Clear navigation data
   */
  clearNavigation(state) {
    state.rawNavigationItems = [];
    state.navigationTree = [];
    state.activeTopBarItemUid = null;
    state.currentRoutePath = null;
    state.activeItemIsBMP = false;
    state.showSidebar = false;
    state.isLoading = false;
    state.hasFatalError = false;
  },
};

const actions = {
  /**
   * Fetch navigation from API and build tree
   * On error, sets fatal error state
   */
  async fetchNavigation({ commit }) {
    commit('setLoading', true);
    commit('setFatalError', false);

    try {
      const items = await platformNavigationService.fetchNavigation();
      commit('setNavigationData', items || []);
    } catch (error) {
      commit('setFatalError', true);
      // Let the component handle redirect to error page
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Set active item based on current route
   * Handles:
   * - Exact matches
   * - Entity pages (e.g., /app/appointments/123 matches /app/appointments)
   * - Route aliases (e.g., /app/scheduler matches /app/calendar)
   */
  setActiveItemFromRoute({ commit, state: storeState }, route) {
    // Skip if navigation data not loaded yet
    if (!storeState.navigationTree || storeState.navigationTree.length === 0) {
      return;
    }

    // Get path from route object (could be route.path or just a string path)
    let currentPath = typeof route === 'string' ? route : route.path;

    // Normalize path: router paths don't have /app prefix, but navigation items do
    // Add /app prefix if missing to match navigation item URLs
    if (currentPath && !currentPath.startsWith('/app/') && currentPath.startsWith('/')) {
      currentPath = `/app${currentPath}`;
    }

    // Store current route path for sidebar item highlighting (include full path with query params)
    commit('setCurrentRoutePath', currentPath);

    // Use findBestMatchingItem which handles exact, prefix, and alias matching
    // It will strip query params internally for comparison
    const item = findBestMatchingItem(storeState.navigationTree, currentPath);

    if (item) {
      // Find the top-level parent of this item
      const topLevelParent = findTopLevelParent(storeState.navigationTree, item.uid);
      if (topLevelParent) {
        commit('setActiveTopBarItem', topLevelParent.uid);
      } else {
        // Item might be a top-level item itself
        const topLevelItems = getTopLevelItems(storeState.navigationTree);
        const isTopLevel = topLevelItems.some((tl) => tl.uid === item.uid);
        if (isTopLevel) {
          commit('setActiveTopBarItem', item.uid);
        }
      }
    } else if (!storeState.activeTopBarItemUid) {
      const bmpItem = findItemByProperty(storeState.navigationTree, 'isBMP', true);
      if (bmpItem) {
        commit('setActiveTopBarItem', bmpItem.uid);
      }
    }
  },

  /**
   * Handle top bar navigation item click
   * External URLs (starting with http:// or https://) open in new tab
   * Internal URLs use handleNavigation utility
   * Items with children redirect to first child
   * BMP items trigger old menu loading
   */
  handleItemClick({ commit, dispatch, state: storeState, rootGetters }, { item }) {
    if (!item) return;

    // Logo/root click: navigate to dashboard but select the BMP item
    const rootUid = getRootItem(storeState.navigationTree)?.uid;
    const isRootItem = item.parent_uid === null || (item.uid != null && item.uid === rootUid);
    if (isRootItem) {
      const bmpItem = findItemByProperty(storeState.navigationTree, 'isBMP', true);
      if (bmpItem) {
        commit('setActiveTopBarItem', bmpItem.uid);
        const oldMenuLoaded = rootGetters['MainMenuStore/allMenuItemsLoaded'];
        if (!oldMenuLoaded) {
          dispatch('MainMenuStore/aggregateMenuItemsForStaff', null, { root: true });
        }
      }
      const targetUrl = item.url || '/app/dashboard';
      const normalizedPath = targetUrl.startsWith('/app/') ? targetUrl.replace(/^\/app/, '') : targetUrl;
      handleNavigation({ path: normalizedPath });
      return;
    }

    if (item.action?.name && !hasChildren(item) && !item.isBMP) {
      const isInPage = item.action.name === 'navigate' && item.action.payload?.newTab !== true;
      if (isInPage) {
        commit('setActiveTopBarItem', item.uid);
      }
      return;
    }

    const itemUrl = item.url || item.action?.payload?.path;

    if (!itemUrl && !hasChildren(item) && !item.isBMP) return;

    if (isExternalUrl(itemUrl)) {
      window.open(itemUrl, '_blank');
      return;
    }

    const isBMP = item.isBMP === true;

    let targetUrl = itemUrl;
    if (!isBMP && hasChildren(item)) {
      const navigableChild = item.children.find((child) => getItemNavigationUrl(child));
      if (navigableChild) {
        targetUrl = getItemNavigationUrl(navigableChild);
      }
    }

    commit('setActiveTopBarItem', item.uid);

    if (isBMP) {
      // BMP items navigate to their own URL and also load the old menu.
      // executeAction is guarded with !item.isBMP in executeItemClick so there is no double-navigation.
      const oldMenuLoaded = rootGetters['MainMenuStore/allMenuItemsLoaded'];
      if (!oldMenuLoaded) {
        dispatch('MainMenuStore/aggregateMenuItemsForStaff', null, { root: true });
      }
    }

    if (targetUrl) {
      commit('setCurrentRoutePath', targetUrl);
      const normalizedPath = targetUrl.startsWith('/app/') ? targetUrl.replace(/^\/app/, '') : targetUrl;
      handleNavigation({ path: normalizedPath });
    }
  },

  /**
   * Handle sidebar item click
   * Just navigates without changing the active top bar item
   */
  handleSidebarItemClick(context, { item }) {
    if (!item) return;
    if (item?.action?.name) return;
    if (!item.url) return;

    if (isExternalUrl(item.url)) {
      window.open(item.url, '_blank');
      return;
    }

    handleNavigation({ path: item.url });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
